import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CommaNumber_NoDecimal,
  CommaNumber_WithDecimal,
  WorkflowWithJustification,
} from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import TimeLabel from '../../common/TimeLabel';
import UserLink from '../../common/links/UserLink';
import CompanyLink from '../../common/links/CompanyLink';
import PlatformLink from '../../common/links/PlatformLink';
import { Table } from '../../common/Table';
import type { TableColumn, TableSettings } from '../../common/Table';

export interface RecentRequestsListProps {
  data: any[];
  hideKeyRequestGroupedDetail: boolean;
}

function RecentRequestsList({
  data,
  hideKeyRequestGroupedDetail,
}: RecentRequestsListProps) {
  const { t } = useTranslation();
  const [tableSettings, setTableSettings] = React.useState<TableSettings>({
    sortedField: { key: 'requestedDate', direction: 'desc' },
    pageNumber: 0,
    pageSize: 100,
  });

  const columns = React.useMemo<TableColumn[]>(
    () => [
      {
        Key: 'reference',
        Label: '#',
        Sort: false,
        Resize: false,
        Render: (row: any) => <Link to={`/request/${row.id}`}>{row.reference}</Link>,
      },
      {
        Key: 'platform',
        Label: t('Platform'),
        Sort: false,
        Resize: false,
        Render: (row: any) => (
          <>
            {row.games && row.games.length > 0
              ? hideKeyRequestGroupedDetail && row.games.length > 1
                ? t('Multiple')
                : (row.games as any[]).map((x: any, i: number) => (
                    <PlatformLink
                      id={x.platformId}
                      name={x.platform}
                      key={`${row.id},${x.platformId}`}
                      className={i < row.games.length - 1 ? 'mb-4' : ''}
                    />
                  ))
              : ''}
          </>
        ),
      },
      {
        Key: 'company',
        Label: t('Company'),
        Sort: false,
        Resize: false,
        Render: (row: any) => <CompanyLink id={row.companyId} name={row.companyName} />,
      },
      {
        Key: 'currentRequestValue',
        Label: t('Value'),
        Sort: false,
        Resize: false,
        className: 'text-right num no-wrap',
        Render: (row: any) =>
          row.currentRequestValue != null
            ? CommaNumber_WithDecimal(row.currentRequestValue, undefined)
            : '',
      },
      {
        Key: 'numberOfKeys',
        Label: t('Keys'),
        Sort: false,
        Resize: false,
        className: 'text-right num no-wrap',
        Render: (row: any) => CommaNumber_NoDecimal(row.numberOfKeys),
      },
      {
        Key: 'workflow',
        Label: t('Type'),
        Sort: false,
        Resize: false,
        Render: (row: any) => WorkflowWithJustification(row),
      },
      {
        Key: 'requestedBy',
        Label: t('Requested by'),
        Sort: false,
        Resize: false,
        Render: (row: any) => (
          <>
            <UserLink id={row.requestedById} name={row.requestedByFullName} />
            <br />
            {row.requestedByCompanyName}
          </>
        ),
      },
      {
        Key: 'requestedDate',
        Label: t('Requested'),
        Sort: false,
        Resize: false,
        Render: (row: any) => (
          <>
            <div>
              <FormattedDate value={row.requestedDate} variant="oneLine" />
            </div>
            <TimeLabel
              elapsedMinutesFormatted={row.elapsedMinutesFormatted}
              breached={row.isSLABreached}
              title={t('Time between request and completion')}
              ongoing={row.requestCompletionDate ? false : true}
            />
          </>
        ),
      },
    ],
    [hideKeyRequestGroupedDetail, t]
  );

  const rows = React.useMemo(
    () => data.map((row) => ({ Key: row.id, ...row })),
    [data]
  );

  return (
    <Table
      columns={columns}
      allColumns={undefined as unknown as string[]}
      rows={rows}
      totalRowCount={rows.length}
      filteredRowCount={rows.length}
      tableSettings={tableSettings}
      setTableSettings={setTableSettings}
      filterChange={() => {}}
      filters={{}}
      className="td-align-top table-sm"
      testId="insights-recent-requests-table"
    />
  );
}

export default RecentRequestsList;
